import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createAdminNote, getAdminNotes, selectAdminNotes, updateAdminNote } from "../ManageUserPage/adminNotes.ducks";
import { selectUserInfo } from "../../Auth/auth.ducks";
import { Button, Col, Form, Icon, Input, Modal, Row, Tooltip } from "antd";
import { AdminNoteRequest, AdminNotesListParsed } from "../../../models";
import { AutoResizeTextArea } from "../../../components/AutoResizeTextArea";

import PaginationLog from "../Log/PaginationLog";
import { Dispatch } from "redux";
import { UserI } from "../../../typings/interfaces";

const defaultSettings = {
  form: {
    id: {
      label: "ID",
      field: "id",
      defaultValue: null,
      rules: [
        {
          required: false,
        }
      ]
    },
    entityId: {
      label: "ID entiteta",
      field: "entityId",
      defaultValue: null,
      rules: [
        {
          required: true,
          message: "Unesite ID entiteta"
        }
      ]
    },
    note: {
      label: "Bilješka",
      field: "note",
      rowCount: 5,
      rules: [
        {
          min: 4,
          required: true,
          message: "Unesite bilješku"
        }
      ]
    },
    submitButton: {
      text: "Dodaj"
    },
    submitEditButton: {
      text: "Uredi"
    },
    cancelButton: {
      text: "Odustani"
    }
  },
  modal: {
    title: 'Dodaj bilješku za korisnika "{0}"'
  }
};

const AdminNotes = (props) => {
  const {
    show,
    entityId,
    entityName,
    form,
    settings,
    onClose
  } = props;

  const noteEditTooltip = <span>{"Uredi"}</span>;

  const dispatch: Dispatch = useDispatch();
  const currentUser : UserI = useSelector(selectUserInfo);
  const adminNotes : AdminNotesListParsed = useSelector(selectAdminNotes);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    show && setIsModalVisible(true);
    return () => setIsModalVisible(false);
  }, [show]);

  useEffect(() => {
    if (show && entityId) {
      setIsLoading(true);
      dispatch(
        getAdminNotes({
          entityId,
          page
        }, () => setIsLoading(false))
      );
    }
  }, [show, entityId]);

  const handleModalOk = () => {
    form.validateFields((err, values) => {
      if (!err) {
        isEditing ? handleUpdate(values) : handleSubmit(values);
        setIsEditing(false);
        form.resetFields();
      }
    });
  };

  const handleSubmit = (values: AdminNoteRequest) => {
    setPage(1);
    setIsLoading(true);
    dispatch(
      createAdminNote(values, success => {
        success && dispatch(
          getAdminNotes({
            page: 1,
            entityId
          }, () => setIsLoading(false))
        );

      })
    );
  };

  const handleUpdate = (values: AdminNoteRequest) => {
    setIsLoading(true);

    dispatch(
      updateAdminNote(values.id, values, success => {
        success && dispatch(
          getAdminNotes({
            page,
            entityId
          }, () => setIsLoading(false))
        );
      })
    );
  }

  const handleModalCancel = () => {
    onClose();

    form.resetFields();
    setIsEditing(false);
  }

  const handlePageChange = (page: number) => {
    setPage(page);
    setIsLoading(true);

    dispatch(
      getAdminNotes({
        entityId,
        page
      }, () => setIsLoading(false))
    );
  };

  const handleEditClick = (value, record) => {
    setIsEditing(true);

    form.setFieldsValue({
      note: record.note,
      entityId: record.entityId,
      id: record.id,
    });
  }

  const handleEditCancel = () => {
    setIsEditing(false);
    form.resetFields();
  }

  return (
    <React.Fragment>
      <Modal
        title={settings.modal.title.replace("{0}", entityName)}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={isEditing ? settings.form.submitEditButton.text : settings.form.submitButton.text}
        cancelText={settings.form.cancelButton.text}
        width={800}
      >
        <Form>
          <Row gutter={0}>
            <Col span={24}>
              <Form.Item style={{ display: "none" }}>
                {form.getFieldDecorator("id", {
                  initialValue: settings.form.id.defaultValue,
                  rules: settings.form.id.rules,
                })(
                  <Input type="hidden" />
                )}
              </Form.Item>
              <Form.Item style={{ display: "none" }}>
                {form.getFieldDecorator("entityId", {
                  initialValue: entityId,
                  rules: settings.form.entityId.rules,
                })(
                  <Input type="hidden" />
                )}
              </Form.Item>
              <span className="control-title required">
                {settings.form.note.label}
              </span>
              <Form.Item>
                {form.getFieldDecorator(settings.form.note.field, {
                  rules: settings.form.note.rules,
                })(
                  <AutoResizeTextArea
                    title={settings.form.note.label}
                    rows={settings.form.note.rowCount}
                    style={{ height: "123px", background: isEditing ? "#FEF2F0" : undefined }}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <PaginationLog
          columns={[
            {
              title: "Datum",
              dataIndex: "createdAt",
              key: "createdAt",
              width: 110,
            },
            {
              title: "Admin",
              dataIndex: "userName",
              key: "userName",
              width: 110,
            },
            {
              title: "Bilješka",
              dataIndex: "note",
              key: "note",
            },
            {
              title: "Izmjena",
              dataIndex: "lastModifiedDate",
              key: "lastModifiedDate",
              width: 110,
              render: (value, record) => {
                return record.lastModifiedDate ? record.lastModifiedDate : "N/A";
              }
            },
            {
              title: "Akcije",
              key: "block",
              align: "center",
              dataIndex: "id",
              width: 55,
              render: (value, record) => (

                <Tooltip placement="topLeft" title={noteEditTooltip}>
                  {record.userId === currentUser.userId ?
                    (isEditing && form.getFieldValue("id") === value ?
                      <Button
                        type="default"
                        onClick={handleEditCancel}
                      >
                        <Icon type="edit" className="manage-ad-icon" />
                      </Button>
                      :
                      <Button
                        type="primary"
                        onClick={() => handleEditClick(value, record)}
                      >
                        <Icon type="edit" className="manage-ad-icon" />
                      </Button>
                    ) :
                    <Button type="default" disabled>
                      <Icon type="edit" className="manage-ad-icon" />
                    </Button>
                  }
                </Tooltip>
              )
            }
          ]}
          loading={isLoading}
          log={adminNotes.notes}
          handlePageChange={handlePageChange}
          responsiveProps={{
            xs: 24
          }}
          emptyLogText={"Nema loga za prikazat."}
        />
      </Modal>
    </React.Fragment>
  );
};

AdminNotes.defaultProps = {
  show: false,
  settings: defaultSettings,
  entityId: null,
  entityName: null,
};

export default Form.create()((AdminNotes));




// WEBPACK FOOTER //
// ./src/routes/Admin/AdminNotes/AdminNotes.tsx